import { useEffect, useMemo } from 'react';
import { jsonToCSV } from 'react-papaparse';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, generatePath } from 'react-router-dom';

import { uniq } from 'lodash';

import { Utils, TTableColumn, Table, Pagination } from '@treezor/design-system';

import { EmptyState } from '@/ui-kit/templates/EmptyState/EmptyState';

import { getPredefRestrictionsGroupMetadata } from '@/store/actions/predef-restrictions-group.actions';
import { openModal } from '@/store/actions/core.actions';
import { selectMetadataByRestrictionsGroupId } from '@/store/selectors';
import { IAddRestrictionInGroupProps } from '@/pages/admin/restrictionGroup/AddRestrictionInGroupModal';
import { RestrictionGroupManagementActions } from '@/pages/admin/restrictionGroup/actions/RestrictionGroupMetadataActions';
import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';
import { useCursor } from '@/hooks/useCursor';
import { useRoutepaths } from '@/router/hooks/useRoutePaths';
import { TableSkeleton } from '@/ui-kit/loaders/TableSkeleton';
import { Page } from '@/ui-kit/templates/Page';

export interface IMids {
  keep: string[];
  remove: string[];
}

export const RestrictionGroupMetadata = () => {
  const { t } = useTranslation();
  const PathRoute = useRoutepaths();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { groupId } = useParams();

  const groupMetadata = useAppSelector(
    (state) => selectMetadataByRestrictionsGroupId(state, 'mid', Number(groupId))?.metadata,
  );

  const isLoading = useAppSelector(
    (state) => selectMetadataByRestrictionsGroupId(state, 'mid', Number(groupId))?.isLoading,
  );

  const cursor = useAppSelector((state) => selectMetadataByRestrictionsGroupId(state, 'mid', Number(groupId))?.cursor);

  const handlePagination = (cursorId) => {
    if (cursorId) dispatch(getPredefRestrictionsGroupMetadata('mid', Number(groupId), cursorId));
  };

  const pagination = useCursor(cursor, 50, handlePagination);

  useEffect(() => {
    if (!Number(groupId)) return;

    dispatch(getPredefRestrictionsGroupMetadata('mid', Number(groupId)));
  }, [groupId, dispatch]);

  const toggleRestrictionGroupUploadMetadataDialog = () => {
    dispatch(openModal('restrictionGroupUploadMetadata'));
  };

  const toggleAddRestrictionGrpDialog = () => {
    dispatch(openModal<IAddRestrictionInGroupProps>('addRestrictionGroup', { restrictionsGroupId: Number(groupId) }));
  };

  const headerKeys = useMemo<string[]>(
    () => uniq(groupMetadata?.reduce((acc, metadata) => [...acc, ...Object.keys(metadata)], [] as string[])),
    [groupMetadata],
  );

  const headers: TTableColumn<any>[] = [...headerKeys, 'actions']?.map((field) => ({
    name: field,
    field,
    label: field === 'actions' ? '' : field,
  }));

  const rows = (groupMetadata || []).map((metadata) => ({
    ...metadata,
    actions: <RestrictionGroupManagementActions mid={String(metadata.mid)} />,
  }));

  const dataCSV = useMemo(() => {
    if (!groupMetadata) return;

    const headerKeyWithoutActions = headerKeys.filter((key) => key !== 'Actions');
    return Object.keys(groupMetadata)?.map((midId) => {
      return headerKeyWithoutActions.reduce((acc, key) => {
        if (!acc[key]) acc[key] = '';
        acc[key] = groupMetadata[midId][key];
        return acc;
      }, {});
    });
  }, [headerKeys, groupMetadata]);

  return (
    <Page
      title={t('pages.administration.restrictionGroup.groupMetadata.title')}
      actions={[
        {
          id: 'add-mid-metadata-btn',
          label: t('pages.cores.add'),
          icon: 'add',
          variant: 'tertiary',
          size: 's',
          onClick: toggleAddRestrictionGrpDialog,
        },
        {
          id: 'export-metadata-btn',
          label: t('pages.cores.export'),
          icon: 'file_export',
          variant: 'tertiary',
          size: 's',
          onClick: () => {
            const csv = jsonToCSV(dataCSV);
            Utils.download(csv, `MidGroup-${groupId}`, 'text/csv');
          },
        },
        {
          id: 'import-metadata-btn',
          label: t('pages.cores.import'),
          icon: 'download',
          variant: 'tertiary',
          size: 's',
          onClick: toggleRestrictionGroupUploadMetadataDialog,
        },
      ]}
      backAction={{
        onClick: () =>
          navigate(
            generatePath(PathRoute.app.root, {
              '*': PathRoute.app.restrictions.main,
            }),
          ),
        isInline: true,
      }}
    >
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <Table
          columns={headers}
          rows={rows}
          emptyComponent={
            <EmptyState title={t('components.molecules.table.noRecord')} icon="search_v1" size="m" className="p-6" />
          }
        />
      )}
      {(!!cursor?.next || !!cursor?.prev) && (
        <Pagination
          {...pagination}
          isFirstButtonVisible
          isLastButtonVisible={false}
          isPagesVisible={false}
          className="flex justify-end w-full"
          isDisabled={false}
        />
      )}
    </Page>
  );
};

export default RestrictionGroupMetadata;
